import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../../services/apiService';
import { useNotificationService } from '../../../services/notificationService';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';
import PropTypes from 'prop-types';
import { appConfigService } from '../../../services/appConfig';
import Modal from '../foundation/Modal';

const NoticeBar = ({ authState, user, loading }) => {
  const { semantic } = useColors();
  const { replace } = useTextReplacement();
  const [isSending, setIsSending] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: 'success', // 'success' or 'error'
    message: ''
  });

  // Check if notifications feature is enabled
  const isNotificationsEnabled = appConfigService.isFeatureEnabled('notifications');

  // Only call hook if notifications feature is enabled
  const { unreadCount, notifications, loading: notificationsLoading } = useNotificationService(
    isNotificationsEnabled ? (user?.internalId || null) : null
  );

  // Calculate total unread count including notifications for other users
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  useEffect(() => {
    if (!notifications) return;

    // Count all unread notifications
    const unreadNotifications = notifications.filter(notification => !notification.isRead);
    setTotalUnreadCount(unreadNotifications.length);
  }, [notifications]);

  const handleSendVerificationEmail = async (e) => {
    e.preventDefault();

    if (isSending) return; // Prevent double-clicks

    setIsSending(true);
    try {
      await apiService.sendVerificationEmail(user?.id);
      setModalState({
        open: true,
        type: 'success',
        message: 'A verification email has been sent to your inbox. Please check your email and click the verification link.'
      });
    } catch (error) {
      setModalState({
        open: true,
        type: 'error',
        message: 'We couldn\'t send the verification email. Please try again later or contact support if the problem persists.'
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleCloseModal = () => {
    setModalState({ ...modalState, open: false });
  };

  // Don't render anything while loading or if auth state is not determined
  if (loading || !authState) {
    return null;
  }

  // Check if we should show email verification notice
  const shouldShowVerification = authState === 'authenticated' && !user?.verified;

  // Early return if notifications feature is disabled
  if (!isNotificationsEnabled) {
    if (shouldShowVerification) {
      return (
        <>
          {/* Email Verification Notice Bar */}
          <div
            className="border-b border-amber-200 shadow-sm"
            style={{
              position: 'fixed',
              top: '62px',  // Below the header
              left: 0,
              right: 0,
              zIndex: 999,
              padding: '10px 16px',
              textAlign: 'center',
              backgroundColor: '#fef3c7',  // Light amber
            }}
            role="alert"
            aria-live="polite"
          >
            <span className="text-amber-900">
              <span className="mr-2" aria-hidden="true">⚠️</span>
              Your account email has not been verified.{' '}
              <button
                onClick={handleSendVerificationEmail}
                disabled={isSending}
                className="inline-flex items-center px-2 py-1 rounded text-amber-800 font-semibold bg-amber-200 hover:bg-amber-300 active:bg-amber-400 border-none cursor-pointer transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Send verification email"
              >
                {isSending ? 'Sending...' : 'Click here to send a verification email'}
              </button>
            </span>
          </div>

          {/* Modal for feedback messages - only render when open */}
          {modalState.open && (
            <Modal
              open={modalState.open}
              title={modalState.type === 'success' ? 'Email Sent!' : 'Unable to Send Email'}
              onClose={handleCloseModal}
              onConfirm={handleCloseModal}
              confirmText="OK"
              showCancelButton={false}
              size="sm"
            >
              <div className={`flex items-start gap-3 ${modalState.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>
                <span className="text-2xl" aria-hidden="true">
                  {modalState.type === 'success' ? '✅' : '❌'}
                </span>
                <p className="m-0 text-neutral-700 dark:text-neutral-300">
                  {modalState.message}
                </p>
              </div>
            </Modal>
          )}
        </>
      );
    }
    return null;
  }

  // Show email verification notice if user is authenticated but not verified
  if (shouldShowVerification) {
    return (
      <>
        <div
          className="border-b border-amber-200 shadow-sm"
          style={{
            position: 'fixed',
            top: '62px',
            left: 0,
            right: 0,
            zIndex: 999,
            padding: '10px 16px',
            textAlign: 'center',
            backgroundColor: '#fef3c7',
          }}
          role="alert"
          aria-live="polite"
        >
          <span className="text-amber-900">
            <span className="mr-2" aria-hidden="true">⚠️</span>
            Your account email has not been verified.{' '}
            <button
              onClick={handleSendVerificationEmail}
              disabled={isSending}
              className="inline-flex items-center px-2 py-1 rounded text-amber-800 font-semibold bg-amber-200 hover:bg-amber-300 active:bg-amber-400 border-none cursor-pointer transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Send verification email"
            >
              {isSending ? 'Sending...' : 'Click here to send a verification email'}
            </button>
          </span>
        </div>

        {modalState.open && (
          <Modal
            open={modalState.open}
            title={modalState.type === 'success' ? 'Email Sent!' : 'Unable to Send Email'}
            onClose={handleCloseModal}
            onConfirm={handleCloseModal}
            confirmText="OK"
            showCancelButton={false}
            size="sm"
          >
            <div className={`flex items-start gap-3 ${modalState.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>
              <span className="text-2xl" aria-hidden="true">
                {modalState.type === 'success' ? '✅' : '❌'}
              </span>
              <p className="m-0 text-neutral-700 dark:text-neutral-300">
                {modalState.message}
              </p>
            </div>
          </Modal>
        )}
      </>
    );
  }

  // Show notification count if there are unread notifications
  if (authState === 'authenticated' && !notificationsLoading && totalUnreadCount > 0) {
    // Separate counts for self and others
    const selfUnreadCount = unreadCount;
    const othersUnreadCount = totalUnreadCount - selfUnreadCount;

    const notificationText = selfUnreadCount > 0
      ? replace(`You have ${selfUnreadCount} personal unread ${totalUnreadCount === 1 ? 'notification' : 'notifications'}`)
      : replace(`You have ${totalUnreadCount} unread ${totalUnreadCount === 1 ? 'notification' : 'notifications'}`);

    return (
      <div className={`container mx-auto m-2 px-4 py-2 flex justify-center align-middle items-center ${semantic.sectionBg} ${semantic.formText} rounded`}>
        <Link
          to="/notifications"
          className={`hover:opacity-80 px-2 border-transparent flex items-center ${semantic.contentCardLink}`}
        >
          <span className="mr-2">
            {notificationText}
            {othersUnreadCount > 0 && ` (${othersUnreadCount} ${replace('for others')})`}
          </span>
          <span className={`${semantic.contentCardBg} ${semantic.contentCardText} rounded-full w-6 h-6 flex items-center justify-center font-bold`}>
            {totalUnreadCount}
          </span>
        </Link>
      </div>
    );
  }

  return null;
};

NoticeBar.propTypes = {
  authState: PropTypes.string,
  user: PropTypes.object,
  loading: PropTypes.bool
};

export default NoticeBar;
